import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { take, takeUntil, takeWhile } from 'rxjs/operators';
import { ConfirmationDialogComponent } from '../../../../../helper/confirmation-dialog-component/confirmation-dialog.component';
import { AnessaTranslationService } from '../../../../../helper/translation/anessa-translation.service';
import { DefaultAssumptionsService } from '../../../../project-input/default-assumptions.service';
import { FeedstockService } from '../../../feedstock.service';
import { Feedstock } from '../../../../../models/project/feedstock/feedstock.model';
import { FeedstockBmpType } from '../../../../../models/project/feedstock/feedstock-bmp-potential/feedstock-bmp-potential.model';
import { BmpYieldType } from '../../../../../models/project/feedstock/feedstock-bmp-potential/feedstock-bmp-potential-type-models/feedstock-bmp-potential-yield.model';
import { UnitValue } from '../../../../../models/data-structures/unit-value.model';
import { FeedstockCharacteristicsPropName } from '../../../../../models/project/feedstock/feedstock-characteristics.model';
import { ReplaySubject } from 'rxjs';
import { UserpilotReloader } from '../../../../../helper/userpilot-reloader.component';

export enum BiogasPotentialOptions {
  BIOGAS_YIELD = 'PROJECT.FEEDSTOCK.CHARACTERISTICS.BIOGAS_POTENTIAL.BIOGAS_YIELD',
  BIOMETHANE_YIELD = 'PROJECT.FEEDSTOCK.CHARACTERISTICS.BIOGAS_POTENTIAL.BIOMETHANE_YIELD',
  BMP = 'PROJECT.FEEDSTOCK.CHARACTERISTICS.BIOGAS_POTENTIAL.BMP',
}

@Component({
  selector: 'app-feedstock-characteristics',
  templateUrl: './feedstock-characteristics.component.html',
  styleUrls: ['./feedstock-characteristics.component.scss']
})
export class FeedstockCharacteristicsComponent extends  UserpilotReloader implements OnInit, OnDestroy {
  @ViewChild('characteristicsForm') characteristicsForm: NgForm;

  @Input() projectInputDisplay = false;
  /** The current feedstock instance */
  @Input() feedstock: Feedstock;
  /** A notifier that emits <null> when a change is made to the current feedstock */
  @Output() changesMadeNotifier = new EventEmitter();
  @Output() formIsValid = new EventEmitter<boolean>();
  /** A notifier that emits when the component is destroyed. This primary purpose of this emitter
   * is to clean up subscriptions */
  private onDestroyNotifier = new ReplaySubject(1);

  /** trigger for translation pipe */
  trigger: string;
  codOrigin = 'characteristics';

  biogasPotentialOptions: BiogasPotentialOptions[] = [];
  selectedBiogasPotential: BiogasPotentialOptions;

  biogasPotentialEnum = BiogasPotentialOptions;

  showClearBiogasPotential = false;

  propEnum = FeedstockCharacteristicsPropName;

  /**
   * Boolean for is the current digester type is dry batch.
   */
  digesterTypeIsDryBatch: boolean;

  constructor(private feedstockService: FeedstockService,
              private dialog: MatDialog,
              private assumptionsService: DefaultAssumptionsService,
              public translationService: AnessaTranslationService
  ) {
    super();
  }

  /**
   * Created a subscription to the codChangedNotifier and updated the
   * the current feedstocks characteristics COD input when its updated
   */
  ngOnInit(): void {
    this.feedstockService.feedstockServiceReadyNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .pipe(takeWhile(x => !x, true))
      .subscribe(ready => {
        if (ready) {
          this.digesterTypeIsDryBatch = this.feedstockService.digesterTypeIsDryBatch();
        }
      });
    this.feedstockService.codChangedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(updatedCod => {
          if (updatedCod && updatedCod.origin !== this.codOrigin && updatedCod.uv) {
            this.feedstock.characteristics.cod = new UnitValue(updatedCod.uv);
          }
        }
      );
    this.translationService.languageChangeNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(() => {
        this.trigger = this.translationService.getCurrentLang();
      });

    this.biogasPotentialOptions = Object.values(BiogasPotentialOptions);

    // Set the dropdown value if applicable
    if (this.feedstock.bmpPotential.type) {
      if (this.feedstock.bmpPotential.type === FeedstockBmpType.BMP_DIRECT) {
        this.selectedBiogasPotential = BiogasPotentialOptions.BMP;
      } else if (this.feedstock.bmpPotential.type === FeedstockBmpType.BIOGAS_YIELD) {
        if (this.feedstock.bmpPotential.bmpYield.yieldType) {
          if (this.feedstock.bmpPotential.bmpYield.yieldType === BmpYieldType.BIOGAS_YIELD) {
            this.selectedBiogasPotential = BiogasPotentialOptions.BIOGAS_YIELD;
          } else if (this.feedstock.bmpPotential.bmpYield.yieldType === BmpYieldType.BIOMETH_YIELD) {
            this.selectedBiogasPotential = BiogasPotentialOptions.BIOMETHANE_YIELD;
          }
        }
      }
    }
  }

  /**
   * Triggers the onDestroyNotifier and unsubscribes from its subscription.
   * This initiates the clean up of all active subscriptions in this component
   */
  ngOnDestroy(): void {
    this.onDestroyNotifier.next(true);
    this.onDestroyNotifier.unsubscribe();
  }

  /**
   * triggers the changes made notifier
   */
  onUnitValueChanged(inputProp: string) {
    if (inputProp === this.propEnum.id_ts && this.feedstock?.autofillPackage?.characteristicsPkg?.ts) {
      if (this.feedstock?.characteristics?.ts?.value !== this.feedstock?.autofillPackage?.characteristicsPkg?.ts?.value) {
        this.feedstock.autofillPackage.characteristicsPkg.ts = null;
      }
    }
    if (inputProp === this.propEnum.id_vs && this.feedstock?.autofillPackage?.characteristicsPkg?.vs) {
      if (this.feedstock?.characteristics?.vs?.value !== this.feedstock?.autofillPackage?.characteristicsPkg?.vs?.value) {
        this.feedstock.autofillPackage.characteristicsPkg.vs = null;
      }
    }
    this.formIsValid.emit(this.characteristicsForm.form.valid);
    this.changesMadeNotifier.emit();
    this.feedstockService.characteristicsChangedNotifier.next(true);
  }

  /**
   * Triggers the COD changed notifier in the FeedstockService. Emits the
   * provided cod UnitValue object.
   * @param cod is the UnitValue that contains the most recent changes made to
   * the COD input
   */
  onCodChanged(cod: UnitValue) {
    this.feedstockService.codChangedNotifier.emit({ origin: this.codOrigin, uv: cod });
  }

  /**
   * Prompts the user for additional confirmation when toggling cod enabled.
   */
  codToggleClicked() {
    if (this.feedstock.characteristics) {
      const status = this.feedstock.characteristics.codEnabled ? this.translationService.get('PROJECT.FEEDSTOCK.CHARACTERISTICS.ENABLING') : this.translationService.get('PROJECT.FEEDSTOCK.CHARACTERISTICS.DISABLING');
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        data: {message: status + this.translationService.get('PROJECT.FEEDSTOCK.CHARACTERISTICS.COD_CHANGE')}
      });

      dialogRef.afterClosed().pipe(take(1)).subscribe(status => {
        if (status !== true) {
          this.feedstock.characteristics.codEnabled = !this.feedstock.characteristics.codEnabled;
        }
      });
    }
  }

  /**
   * prompts the user for additional input when disabling density when the dry batch digester
   * is selected.
   */
  densityToggleClicked() {
    if (this.feedstock.characteristics) {
      const status = this.feedstock.characteristics.densityEnabled ? this.translationService.get('PROJECT.FEEDSTOCK.CHARACTERISTICS.ENABLING') : this.translationService.get('PROJECT.FEEDSTOCK.CHARACTERISTICS.DISABLING');
      let message = status + this.translationService.get('PROJECT.FEEDSTOCK.CHARACTERISTICS.DENSITY_CHANGE.1');
      if (this.feedstock.characteristics.densityEnabled && this.feedstockService.digesterTypeIsDryBatch()) {
        message = this.translationService.get('PROJECT.FEEDSTOCK.CHARACTERISTICS.DENSITY_CHANGE.0') + message;
      }
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        data: {message: message}
      });

      dialogRef.afterClosed().pipe(take(1)).subscribe(status => {
        if (status !== true) {
          this.feedstock.characteristics.densityEnabled = !this.feedstock.characteristics.densityEnabled;
        }
      });
    }
  }

  /**
   * Sets the bmp type and biogas yield type if applicable
   */
  setBmpData() {
    let bmpType: FeedstockBmpType;
    let yieldType: BmpYieldType;
    switch (this.selectedBiogasPotential) {
      case BiogasPotentialOptions.BIOGAS_YIELD:
        bmpType = FeedstockBmpType.BIOGAS_YIELD;
        yieldType = BmpYieldType.BIOGAS_YIELD;
        break;
      case BiogasPotentialOptions.BIOMETHANE_YIELD:
        bmpType = FeedstockBmpType.BIOGAS_YIELD;
        yieldType = BmpYieldType.BIOMETH_YIELD;
        break;
      case BiogasPotentialOptions.BMP:
        bmpType = FeedstockBmpType.BMP_DIRECT;
        yieldType = null;
        break;
    }
    const index = this.feedstockService.getFeedstocks().indexOf(this.feedstock);
    this.assumptionsService.setFeedstockAssumptions(this.feedstock, index, bmpType, yieldType);
  }
}
